import { SystemStyleObject, useMultiStyleConfig } from '@chakra-ui/system';
import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';

import { noop } from '@frontend/utils';

import { EMPTY_SUBMENU } from '../consts';
import { useCascaderComputedValues } from '../hooks/useCascaderComputedValues';
import { useCascaderKeyboardInput } from '../hooks/useCascaderKeyboardInput';
import { useCombobox, type UseComboboxReturn } from '../hooks/useCombobox';
import {
  cascaderActions,
  cascaderReducer,
  createInitialState,
} from '../state/cascaderReducer';
import type {
  CascaderOption,
  CascaderOptionWithPath,
  CascaderProps,
  CascaderCommonProps,
  CascaderSubmenu,
  CascaderOptionGroup,
  CascaderPositioningOptions,
} from '../types';
import { isSubmenu, toIndexPath } from '../util';

export interface CascaderState<OptionType extends CascaderOption>
  extends CascaderProps<OptionType> {
  // Core state
  inputValue: string;
  selectedPath: string[];
  cursorPath: number[];
  highlightedValue: string | null;
  isSearchMode: boolean;
  isOpen: boolean;

  // Computed values
  root: CascaderSubmenu<OptionType>;
  rootGroups: CascaderOptionGroup<OptionType>[];
  rootOptions: OptionType[];
  rootOptionsWithPaths: CascaderOptionWithPath<OptionType>[];
  flatOptions: CascaderOptionWithPath<OptionType>[];
  filteredOptions: CascaderOptionWithPath<OptionType>[];
  selectedOption?: CascaderOptionWithPath<OptionType>;

  // Combobox API
  comboboxApi: UseComboboxReturn['comboboxApi'];

  // Styles
  cascaderStyles: Record<string, SystemStyleObject>;

  // Refs
  inputRef: React.RefObject<HTMLInputElement>;

  // Actions
  setInputValue: (value: string) => void;
  setSelectedPath: (path: string[]) => void;
  setCursorPath: (path: number[]) => void;
  setHighlightedValue: (value: string | null) => void;
  handleSelect: (option: OptionType, fullPath: string[]) => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onRequestClose: () => void;
  onRequestOpen: () => void;
  getOptionsAtLevel: (level: number) => OptionType[];
  onHighlightedIndexChange: (level: number, index: number) => void;
  onMoveToParentLevel: (level: number) => void;
  onMoveToChildLevel: () => void;
  handleSelectOption: (option: OptionType, level: number) => void;
  handleClearSelection: () => void;
  focusInput: () => void;

  getCommonProps: () => CascaderCommonProps<OptionType>;
}

const CascaderStateContext =
  createContext<CascaderState<CascaderOption> | null>(null);

const SEARCH_MODE_DEFAULT_POSITIONING: CascaderPositioningOptions = {
  sameWidth: true,
  placement: 'bottom-start',
};

const MENU_MODE_DEFAULT_POSITIONING: CascaderPositioningOptions = {
  placement: 'bottom-start',
};

export const CascaderStateProvider = <OptionType extends CascaderOption>({
  children,
  ...props
}: CascaderProps<OptionType> & { children: React.ReactNode }) => {
  const {
    options = EMPTY_SUBMENU as CascaderSubmenu<OptionType>,
    value,
    onChange = noop,
    placeholder = 'Select...',
    isDisabled = false,
    isLoading = false,
    size = 'md',
    isClearable = false,
    canSelectSubmenu = false,
    inputRef: inputRefFromProps,
    searchModePositioning = SEARCH_MODE_DEFAULT_POSITIONING,
    menuModePositioning = MENU_MODE_DEFAULT_POSITIONING,
    searchFilter,
    searchSort,
    ...rest
  } = props;

  // ── Core state via reducer ──────────────────────────────────────────

  const [
    {
      inputValue,
      cursorPath,
      highlightedValue,
      isOpen,
      hoverOption,
      lastInteractedType,
      selectedPath: selectedPathFromReducer,
    },
    dispatch,
  ] = useReducer(
    cascaderReducer<OptionType>,
    { value },
    createInitialState<OptionType>,
  );

  const selectedPath = value !== undefined ? value : selectedPathFromReducer;
  // We are in search mode if the input value is not empty, so we should trim it
  const isSearchMode = inputValue.length > 0;

  // ── Refs & focus ────────────────────────────────────────────────────

  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefFromProps ?? internalInputRef;
  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  // ── Computed option collections ─────────────────────────────────────

  const {
    root,
    rootGroups,
    rootOptions,
    rootOptionsWithPaths,
    flatOptions,
    filteredOptions,
    selectedOption,
    cursorOption,
    getOptionAtPath,
    getOptionsAtLevel: getOptionsAtLevelCallback,
  } = useCascaderComputedValues<OptionType>({
    options,
    inputValue,
    selectedPath,
    cursorPath,
    searchFilter,
    searchSort,
  });

  // ── Styles & positioning ────────────────────────────────────────────

  const cascaderStyles = useMultiStyleConfig('Cascader', { size });

  const positioningForMode = useMemo(
    () =>
      isSearchMode
        ? { ...SEARCH_MODE_DEFAULT_POSITIONING, ...searchModePositioning }
        : { ...MENU_MODE_DEFAULT_POSITIONING, ...menuModePositioning },
    [searchModePositioning, menuModePositioning, isSearchMode],
  );

  // ── Combobox integration ────────────────────────────────────────────

  const { comboboxApi } = useCombobox({
    inputRef,
    inputValue,
    disabled: isDisabled || isLoading,
    placeholder,
    filteredOptions,
    positioning: positioningForMode,
    onHighlightChange: (val) =>
      dispatch(cascaderActions.setHighlightedValue(val)),
    onOpenChange: (newIsOpen) => {
      if (newIsOpen) {
        const indexPath =
          selectedPath.length > 0 ? toIndexPath(selectedPath, rootOptions) : [];
        dispatch(cascaderActions.open(indexPath));
      } else {
        dispatch(cascaderActions.close());
      }
    },
    onSelectOption: (option: CascaderOptionWithPath) => {
      const indexPath = toIndexPath(option.fullPath, rootOptions);
      dispatch(cascaderActions.select(option.fullPath, indexPath));
      onChange?.(option.fullPath, option as OptionType);
    },
  });

  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpenRef.current !== isOpen) {
      comboboxApi.setOpen(isOpen);
      prevIsOpenRef.current = isOpen;
    }
  }, [isOpen, comboboxApi]);

  // ── Action callbacks ────────────────────────────────────────────────

  const setInputValue = useCallback(
    (val: string) => dispatch(cascaderActions.setInput(val)),
    [],
  );

  const setSelectedPath = useCallback(
    (path: string[]) => {
      const indexPath = toIndexPath(path, rootOptions);
      dispatch(cascaderActions.select(path, indexPath));
    },
    [rootOptions],
  );

  const setCursorPath = useCallback(
    (path: number[]) => dispatch(cascaderActions.setCursorPath(path)),
    [],
  );

  const setHighlightedValue = useCallback(
    (val: string | null) => dispatch(cascaderActions.setHighlightedValue(val)),
    [],
  );

  const handleSelect = useCallback(
    (option: OptionType, fullPath: string[]) => {
      const indexPath = toIndexPath(fullPath, rootOptions);
      dispatch(cascaderActions.select(fullPath, indexPath));
      onChange?.(fullPath, option);
    },
    [rootOptions, onChange],
  );

  const onRequestClose = useCallback(() => {
    dispatch(cascaderActions.close());
  }, []);

  const onRequestOpen = useCallback(() => {
    const indexPath =
      selectedPath.length > 0 ? toIndexPath(selectedPath, rootOptions) : [];
    dispatch(cascaderActions.open(indexPath));
  }, [selectedPath, rootOptions]);

  const onHighlightedIndexChange = useCallback(
    (level: number, index: number) => {
      dispatch(cascaderActions.navigateToIndex(level, index));
      focusInput();
    },
    [focusInput],
  );

  const onMoveToParentLevel = useCallback(
    (level: number) => {
      dispatch(cascaderActions.navigateParent(level));
      focusInput();
    },
    [focusInput],
  );

  const onMoveToChildLevel = useCallback(() => {
    dispatch(cascaderActions.navigateChild());
    focusInput();
  }, [focusInput]);

  const buildPathToOption = useCallback(
    (option: OptionType, level: number): string[] => {
      const fullPath: string[] = [];
      for (let i = 0; i < level; i++) {
        const levelOptions = getOptionsAtLevelCallback(i);
        const currentIndex = cursorPath[i];
        if (currentIndex >= 0 && currentIndex < levelOptions.length) {
          fullPath.push(levelOptions[currentIndex].value);
        }
      }
      fullPath.push(option.value);
      return fullPath;
    },
    [cursorPath, getOptionsAtLevelCallback],
  );

  const handleSelectOption = useCallback(
    (option: OptionType, level: number) => {
      const currentOptions = getOptionsAtLevelCallback(level);
      const optionIndex = currentOptions.findIndex(
        (opt) => opt.value === option.value,
      );

      if (!isSubmenu(option) || canSelectSubmenu) {
        handleSelect(option, buildPathToOption(option, level));
      } else {
        dispatch(cascaderActions.drillIntoSubmenu(level, optionIndex));
        focusInput();
      }
    },
    [
      getOptionsAtLevelCallback,
      handleSelect,
      focusInput,
      buildPathToOption,
      canSelectSubmenu,
    ],
  );

  const handleClearSelection = useCallback(() => {
    dispatch(cascaderActions.clear());
    onChange?.([], null);
    focusInput();
  }, [onChange, focusInput]);

  // ── Keyboard navigation ─────────────────────────────────────────────

  const { handleInputKeyDown } = useCascaderKeyboardInput({
    isOpen,
    isSearchMode,
    cursorPath,
    canSelectSubmenu,
    getOptionsAtLevel: getOptionsAtLevelCallback,
    onRequestClose,
    handleSelectOption,
    onHighlightedIndexChange,
    onMoveToParentLevel,
    onMoveToChildLevel,
  });

  // ── Derived preview / hover ─────────────────────────────────────────

  const handleSetHoverOption = useCallback((option: OptionType | null) => {
    dispatch(cascaderActions.setHover(option));
  }, []);

  const previewedOption = useMemo(() => {
    if (lastInteractedType === 'hover') return hoverOption;
    if (cursorOption) return cursorOption;
    return null;
  }, [lastInteractedType, cursorOption, hoverOption]);

  const getCommonProps: () => CascaderCommonProps<OptionType> = useCallback(
    () => ({
      getSelectedValue: () => selectedPath,
      getInputValue: () => inputValue,
      hasSelectedValue: selectedPath.length > 0,
      hasInputValue: inputValue.length > 0,
      getCursorPath: () => cursorPath,
      getCursorOption: () => cursorOption,
      getHighlightedSearchValue: () => highlightedValue,
      getSelectedOption: () => selectedOption,
      getOptionAtPath: getOptionAtPath,
      getHoverOption: () => hoverOption,
      setHoverOption: handleSetHoverOption,
      getPreviewedOption: () => previewedOption,
      getIsLoading: () => isLoading,
    }),
    [
      selectedPath,
      inputValue,
      highlightedValue,
      cursorPath,
      cursorOption,
      selectedOption,
      getOptionAtPath,
      hoverOption,
      handleSetHoverOption,
      previewedOption,
      isLoading,
    ],
  );

  // ── Context value assembly ──────────────────────────────────────────

  const state: CascaderState<OptionType> = {
    inputValue,
    selectedPath,
    cursorPath,
    highlightedValue,
    isSearchMode,
    isOpen,

    root,
    rootGroups,
    rootOptions,
    rootOptionsWithPaths,
    flatOptions,
    filteredOptions,
    selectedOption,

    comboboxApi,
    cascaderStyles,
    inputRef,

    setInputValue,
    setSelectedPath,
    setCursorPath,
    setHighlightedValue,
    handleSelect,
    handleInputKeyDown,
    onRequestClose,
    onRequestOpen,
    getOptionsAtLevel: getOptionsAtLevelCallback,
    onHighlightedIndexChange,
    onMoveToParentLevel,
    onMoveToChildLevel,
    handleSelectOption,
    handleClearSelection,
    focusInput,

    getCommonProps,

    isClearable,
    canSelectSubmenu,
    ...rest,
  };

  return (
    <CascaderStateContext.Provider
      value={state as CascaderState<CascaderOption>}
    >
      {children}
    </CascaderStateContext.Provider>
  );
};

export const useCascaderState = <
  OptionType extends CascaderOption,
>(): CascaderState<OptionType> => {
  const context = useContext(CascaderStateContext) as CascaderState<OptionType>;
  if (!context) {
    throw new Error(
      'useCascaderState must be used within a CascaderStateProvider',
    );
  }
  return context;
};
